/**
 * @lines98/client/lib/helper-unlock-overlay — МОДАЛЬНЫЙ ОВЕРЛЕЙ «Открыт новый помощник!»
 * поверх финальной доски на экране результата (переиспользуемый модуль; result-screen
 * монтирует ТОНКО, как maybeMountReward).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-playtest 2026-06-20 «приходится скроллить, чтобы её
 * увидеть» + frontend-lead тикет helper-unlock-overlay-redesign). Прежде уведомление об
 * открытии помощника = секция внутри reward-block (`buildHelperUnlockSection`) → уходило под
 * скролл на экране результата. Перестроено в центрированный модальный оверлей (native
 * <dialog>.showModal → top-layer + ::backdrop + Esc + focus-trap из коробки): ВСЕГДА в кадре,
 * по центру, без скролла, с reveal-церемонией. Карточка = ТОТ ЖЕ примитив gnome-card (вариант
 * unlock-celebration), reveal = lib/gnome-reveal, бейдж-точка рейла = nav-rail.setItemBadge —
 * единые источники (модель «ствол → ветки», §1; bespoke-дубль = REJECT).
 *
 * **Server-truth (I-Gn4):** оверлей ВИЗУАЛИЗИРУЕТ `summary.progression.gnome_unlocks`
 * (server-truth, `Array<{color, milestone_index, milestone_level}>`) — клиент НЕ пересчитывает
 * открытия. Пусто/absent → оверлея нет.
 *
 * **AC-7 (баг-фикс «точка спустя время»):** при finish с непустым gnome_unlocks бейдж-точка
 * на пункте рейла «Помощники» ставится СРАЗУ из submit-ответа — не дожидаясь следующего
 * meta-state poll. Механизм: эмитим свежий granted через общий seam meta-state-events
 * (`emitMetaStateGranted`) — тот же канал, что слушает app.ts-подписчик (`refreshGnomesBadge`),
 * который сравнивает granted с per-player last-seen (lib/gnome-unlock-badge) и ставит dot.
 * granted = last-seen + число новых открытий (открытия — НОВЫЕ этой партией → granted > last-
 * seen гарантированно → dot). server-truth granted цел: его перетрёт следующий getMetaState.
 *
 * **AC-6 (несколько открытий за партию):** очередь карточек ПО ОДНОЙ (закрыл → следующая),
 * порядок = возрастание color (= индекс вехи, §5.1). Тап-карточка на ЛЮБОЙ → переход на
 * «Помощники» (очередь прерывается). Каждое открытие = отдельная reveal-церемония (per-color
 * reveal как в gnome-reveal; счётчик «N новых» обесценил бы reveal).
 *
 * **reduce-motion (AC-13):** reveal мгновенный через DI `reduceMotion` в revealGnomes (НЕ
 * matchMedia внутри плеера) — секрет имени/цвета держится и в финале (O-5).
 *
 * SCOPE: чистые `orderedUnlockColors` / `needsImmediateBadge` (юнит-тестируемо без DOM) +
 * монтаж native-dialog-оверлея (карточка + reveal + очередь + навигация + немедленный бейдж).
 * NOT IN SCOPE: серверная логика gnome_unlocks (server-truth); экран «Помощники»
 * (`gnomes-screen` — только переход); reveal-механика (`lib/gnome-reveal`); вычисление
 * «виден ли бейдж» (`lib/gnome-unlock-badge` — слушает app.ts).
 *
 * Cross-references:
 *   - components/gnome-card.ts (createGnomeCard — карточка-примитив; вариант unlock-celebration).
 *   - lib/gnome-reveal.ts (revealGnomes — reveal-плеер, reduce-motion DI).
 *   - lib/meta-state-events.ts (emitMetaStateGranted — немедленный бейдж через общий seam).
 *   - lib/gnome-unlock-badge.ts (loadLastSeenUnlocks — база для granted; app.ts ставит dot).
 *   - components/confirm-dialog.ts / dialog-plaque.css (образец native-dialog-семейства).
 *   - components/info-popover.ts (descriptive copy за «?» на touch, §0.E).
 *   - helper-unlock-overlay.css (co-located — плашка/карточка sizing, layout-plan).
 */

import './helper-unlock-overlay.css';

import type { ColorIndex, MetaFaucetSummary } from '@lines98/shared/types/game';

import { createGnomeCard } from '../components/gnome-card.js';
import { useGameStore } from '../store/game-store.js';
import { gnomeCardSingleMode } from './gnome-card-fit.js';
import { applyMaxGnomeFrameAspect } from './gnome-frame-aspect.js';
import { revealGnomes, type RevealSlot } from './gnome-reveal.js';
import { loadLastSeenUnlocks } from './gnome-unlock-badge.js';
import { t } from './i18n/index.js';
import { emitMetaStateGranted } from './meta-state-events.js';

// =============================================================================
// Чистые функции (юнит-тестируемо без DOM — helper-overlay-queue.test.ts)
// =============================================================================

/** Один элемент server-truth `gnome_unlocks` (открыто этой партией; §5.1). */
export interface GnomeUnlockEntry {
  readonly color: number;
  readonly milestone_index: number;
  readonly milestone_level: number;
}

/**
 * Цвета открытых помощников в порядке очереди reveal/оверлеев = ВОЗРАСТАНИЕ `color`
 * (= индекс вехи, §5.1, AC-6). Вход НЕ отсортирован; дубли (если прилетят) схлопываются
 * (одна reveal-церемония на цвет). Defensive: не-массив/пусто → []. Чистая функция.
 *
 * @param unlocks — server-truth открытия этой партией (не отсортированы).
 */
export function orderedUnlockColors(unlocks: readonly GnomeUnlockEntry[]): number[] {
  if (!Array.isArray(unlocks)) return [];
  const seen = new Set<number>();
  for (const u of unlocks) {
    if (u && typeof u.color === 'number' && Number.isFinite(u.color)) seen.add(u.color);
  }
  return [...seen].sort((a, b) => a - b);
}

/**
 * Нужен ли бейдж-точка на рейле НЕМЕДЛЕННО (AC-7): список открытий НЕ пуст. Пусто/absent/
 * мусор → false (бейдж не светится на мусоре). Чистая функция (defensive к не-массиву).
 *
 * @param unlocks — server-truth открытия этой партией.
 */
export function needsImmediateBadge(unlocks: readonly GnomeUnlockEntry[]): boolean {
  return Array.isArray(unlocks) && unlocks.length > 0;
}

// =============================================================================
// Монтаж оверлея (DOM) — поведение проверяет helper-unlock-overlay.viewport
// =============================================================================

/** Опции монтажа оверлея открытия помощника. */
export interface HelperUnlockOverlayOptions {
  /**
   * server-truth сводка партии (`MetaFaucetSummary`). Оверлей читает из неё открытия
   * `summary.progression.gnome_unlocks` (server-truth, I-Gn4 — клиент НЕ пересчитывает).
   */
  readonly summary: MetaFaucetSummary;
  /**
   * Мгновенный финал reveal (reduce-motion / тесты, AC-13). По умолчанию уважает
   * `prefers-reduced-motion`. DI (НЕ matchMedia внутри revealGnomes — юнит-тестируемость).
   */
  readonly reduceMotion?: boolean;
}

/** Handle смонтированного оверлея — размонтирование экраном при уходе. */
export interface HelperUnlockOverlayHandle {
  /** Закрыть текущий оверлей и снять очередь (экран ушёл / unmount). */
  unmount(): void;
}

/** Учитывать ли prefers-reduced-motion (без window — тесты — «нет», но опция важнее). */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Смонтировать оверлей открытия помощника. Если открытий нет — no-op (handle с пустым
 * unmount). Иначе:
 *   1) НЕМЕДЛЕННО ставит бейдж-точку рейла (AC-7) через общий seam meta-state-events;
 *   2) показывает очередь карточек ПО ОДНОЙ (AC-6, порядок = orderedUnlockColors);
 *   3) тап-карточка → onOpenGnomes() + закрыть всё (AC-4); backdrop/Esc → следующая (AC-5/6).
 *
 * @param opts — открытия + навигация + reduce-motion (DI).
 */
export function mountHelperUnlockOverlay(
  opts: HelperUnlockOverlayOptions,
): HelperUnlockOverlayHandle {
  // server-truth открытия этой партией (I-Gn4 — читаем из сводки, не пересчитываем).
  const unlocks = opts.summary.progression?.gnome_unlocks ?? [];
  const colors = orderedUnlockColors(unlocks);
  if (colors.length === 0) {
    return { unmount(): void {} };
  }

  const reduceMotion = opts.reduceMotion ?? prefersReducedMotion();

  // --- AC-7: немедленная точка-акцент на рейле «Помощники» (баг-фикс «спустя время»). ---
  // Эмитим свежий granted через ТОТ ЖЕ seam, что слушает app.ts (refreshGnomesBadge). granted
  // = last-seen + число новых открытий: открытия — НОВЫЕ этой партией, поэтому granted > last-
  // seen гарантированно → подписчик поставит dot СРАЗУ (не дожидаясь meta-poll). server-truth
  // granted перетрёт следующий getMetaState (I-Gn4 цел — это лишь немедленный UX-сигнал).
  if (needsImmediateBadge(unlocks)) {
    emitMetaStateGranted(loadLastSeenUnlocks() + colors.length);
  }

  let index = 0; // позиция в очереди оверлеев
  let activeDialog: HTMLDialogElement | null = null;
  let disposed = false; // экран ушёл — больше очередь не продолжаем

  /** Закрыть текущий оверлей (dialog), не трогая очередь. */
  function closeActive(): void {
    if (activeDialog) {
      const d = activeDialog;
      activeDialog = null;
      if (d.open) d.close();
      if (d.parentNode) d.parentNode.removeChild(d);
    }
  }

  /**
   * Перейти на «Помощники» с ФОКУСОМ на тапнутом помощнике (companion-focus AC-2):
   * прервать очередь, закрыть всё, навигировать. `focusColor` — цвет ТАПНУТОЙ
   * карточки очереди (`openOne(color)` его знает) → экран «Помощники» откроется
   * спозиционированным на этом гноме, а не на первом. Тот же store-путь, что
   * рейл-пункт «Помощники» (AC-4 оверлея; не прямой History/URL).
   */
  function goToGnomes(focusColor: ColorIndex): void {
    disposed = true;
    closeActive();
    useGameStore.getState().showGnomes(focusColor);
  }

  /** Показать следующую карточку очереди (или закончить, AC-6). */
  function showNext(): void {
    if (disposed) return;
    if (index >= colors.length) {
      closeActive();
      return;
    }
    const color = colors[index]! as ColorIndex;
    index += 1;
    openOne(color);
  }

  /** Смонтировать ОДИН оверлей для цвета `color` (карточка + reveal + закрытия). */
  function openOne(color: ColorIndex): void {
    const dialog = document.createElement('dialog');
    // helper-unlock-overlay — собственный класс плашки (своя ширина под карточку); семейство
    // native <dialog> showModal (как confirm-dialog / dialog-plaque) — top-layer/::backdrop/Esc.
    dialog.className = 'helper-unlock-overlay';

    // ЕДИНАЯ ширина рамки гнома = как в сетке «Помощники» (owner 2026-06-20 «копия карточки из
    // Помощники, того же размера»). На «Помощниках» экран ставит `--gnc-frame-aspect` (max аспект
    // по всем 7 PNG) на `.gnomes-list` → рамка-герой каждого гнома = по самому широкому. Оверлей
    // не под `.gnomes-list` → без этого гном рисовался в СВОЁМ (более узком) аспекте (рамка уже).
    // Ставим тот же max-аспект из общего модуля (single source) → hero-рамка 1-в-1 с «Помощники».
    void applyMaxGnomeFrameAspect(dialog);

    // owner 2026-06-20 «только карточка»: НЕТ заголовка/✕/описания — оверлей = одна карточка
    // (закрытие тапом мимо/Esc, AC-5/6). a11y: native showModal даёт фокус-трап; aria-label
    // несёт смысл оверлея (заголовка-узла нет).
    dialog.setAttribute('aria-label', t('game.reward.helperUnlockTitle'));

    // --- Карточка-помощник = ТОТ ЖЕ примитив gnome-card (вариант unlock-celebration, AC-2). ---
    // unlocked:true → примитив рисует ОТКРЫТУЮ карточку: цветной спрайт + имя+уровень (topline)
    // + БОНУСЫ + кнопку «Улучшить». owner-playtest 2026-06-20: на оверлее видны бонусы, как на
    // «Помощниках» — это та же открытая карточка, только кнопка «Улучшить» заменена табличкой
    // «Новый помощник». До reveal детали скрыты силуэтом (--unlock-silhouette: dim спрайт +
    // visibility:hidden topline/bonuses), reveal-settle снимает класс → открытая карточка целиком.
    const cardHandle = createGnomeCard({
      state: { color, level: 1, prestigeCount: 0, unlocked: true },
      // onAction/onOpenDetail НЕ передаём — кнопку «Улучшить» убираем ниже (её место — табличка);
      // тап по всей карточке навигирует (наш обработчик ниже, §8.7 — нет конкурирующей кнопки).
    });
    const card = cardHandle.element;
    card.classList.add('gnome-card--unlock-celebration');
    // Силуэт С САМОГО НАЧАЛА (чтобы открытая карточка не «мелькнула» до вспышки): dim спрайт +
    // visibility:hidden topline/bonuses (CSS .gnome-card--unlock-silhouette). reveal-settle снимет
    // класс → открытая карточка (бонусы видны, как на «Помощниках»).
    card.classList.add('gnome-card--unlock-silhouette');
    card.dataset.color = String(color); // явно (data-color для проверки порядка очереди)

    // Убрать кнопку «Улучшить» (открытая карточка её рендерит) — на её место встаёт табличка
    // «Новый помощник». Прокачка делается на «Помощниках», не из оверлея.
    const actionBtn = card.querySelector<HTMLElement>('.gnome-card-action');
    if (actionBtn) actionBtn.remove();
    // Снять role/tabindex/aria locked-карточки (тап навигирует через наш handler, не «teaser»).
    card.removeAttribute('role');
    card.removeAttribute('tabindex');
    card.removeAttribute('aria-label');

    // Табличка «Новый помощник» (AC-3): НЕ-кнопка (нет click/hover/tab-order), габариты = слоту
    // кнопки «Улучшить» (CSS .gnome-card-new-helper-label). Вставляем НАД статус-строкой (там,
    // где у открытой карточки стоит .gnome-card-action). Текст — локализованный (ru/en).
    const newHelperLabel = document.createElement('div');
    newHelperLabel.className = 'gnome-card-new-helper-label';
    newHelperLabel.textContent = t('game.reward.newHelper');
    newHelperLabel.setAttribute('aria-hidden', 'true'); // факт открытия несёт заголовок оверлея
    const statusEl = card.querySelector<HTMLElement>('.gnome-card-status');
    if (statusEl) card.insertBefore(newHelperLabel, statusEl);
    else card.appendChild(newHelperLabel);

    // Тап/Enter по карточке → переход на «Помощники» (AC-4; очередь прервана, AC-6). §8.7:
    // celebration-карточка не имеет конкурирующих icon-button → клик по всей карточке легален.
    card.classList.add('gnome-card-zone');
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', t('game.reward.helperUnlockTitle'));
    // Прокидываем ЦВЕТ этой карточки очереди (AC-2): фокус ляжет на тапнутого гнома.
    card.addEventListener('click', () => goToGnomes(color));
    card.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        goToGnomes(color);
      }
    });

    dialog.appendChild(card);

    // owner 2026-06-20: описание убрано («лишний текст») — под карточкой ничего нет.

    // backdrop-тап (e.target === dialog) → закрыть текущий → следующий (AC-5/6).
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        closeActive();
        showNext();
      }
    });
    // Esc — native «cancel» → трактуем как «закрыть текущий» → следующий (AC-5/6).
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeActive();
      showNext();
    });

    document.body.appendChild(dialog);
    activeDialog = dialog;
    dialog.showModal();

    // КРУПНЫЙ (single) режим = РОВНО как на «Помощниках» (owner-playtest 2026-06-20 «того же
    // размера»): на узком телефоне сетка «Помощники» растит карточку в --single → оверлей-карточка
    // должна совпадать. Решение «<2 комфортных карточек влезает» — ОБЩИЙ модуль gnome-card-fit
    // (тот же расчёт, что у gnomes-screen). Замер: натуральная ширина карточки (со снятым --single)
    // vs доступная ширина оверлея (dialog content-box = вьюпорт − 2·--m) и зазор --m. RO — поворот/
    // ресайз; disconnect по native 'close' (единый путь закрытия — closeActive() зовёт dialog.close).
    const px = (v: string): number => parseFloat(v) || 0;
    const updateSingle = (): void => {
      dialog.classList.remove('helper-unlock-overlay--single');
      const cardW = card.getBoundingClientRect().width;
      const gap = px(getComputedStyle(document.documentElement).getPropertyValue('--m'));
      const avail = dialog.clientWidth;
      dialog.classList.toggle('helper-unlock-overlay--single', gnomeCardSingleMode(avail, cardW, gap));
    };
    updateSingle();
    const ro = new ResizeObserver(() => updateSingle());
    ro.observe(dialog);
    dialog.addEventListener('close', () => ro.disconnect());

    // --- Reveal-церемония: силуэт → вспышка → цветной персонаж (lib/gnome-reveal, AC-2/13). ---
    // Слот = карточка целиком (scale-вспышка от center, GPU-only — соседей нет). onSettled
    // снимает celebration-силуэт-класс → CSS убирает силуэт-filter спрайта (цвет проявляется;
    // имя/уровень/бонусы остаются скрыты вариантом — секрет O-5). reduce-motion → мгновенно
    // (DI reduceMotion; без класса --revealing, AC-13). Fire-and-forget: уход с экрана прерывает.
    card.classList.add('gnome-card--unlock-silhouette');
    const slot = card as unknown as RevealSlot;
    void revealGnomes([slot], {
      reduceMotion,
      onSettled: (s) => {
        (s as unknown as HTMLElement).classList.remove('gnome-card--unlock-silhouette');
      },
    });
  }

  // Запустить очередь с первой карточки (наименьший цвет).
  showNext();

  return {
    unmount(): void {
      disposed = true;
      closeActive();
    },
  };
}
